import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Evaluates a question's `displayIf` rules against the current answer bag.
 * A question with no `displayIf` is always visible. This is pure
 * condition-evaluation logic (not pricing/business logic) - it belongs
 * here rather than in a UI package so any host (this app, a future one,
 * or a test) can compute "what should currently be asked/answered" the
 * same way, and so the wizard framework can use it generically without
 * knowing anything about a specific project type's questions.
 *
 * A controlling answer can be either a single-select's string value or a
 * multi_select's string array - `equalsAnyOf` is matched against both the
 * same way: a string answer is visible if it's one of the listed values,
 * an array answer is visible if it contains any of the listed values.
 * (Originally this only handled the string case, which meant a question
 * couldn't be gated on a multi_select answer at all - see basement's room
 * composition question in `questions/basement.ts` for the motivating case.)
 */
export function isQuestionVisible(question: QuestionDefinition, answers: Record<string, unknown>): boolean {
  if (!question.displayIf || question.displayIf.length === 0) return true;
  return question.displayIf.every((rule) => {
    const value = answers[rule.questionId];
    if (typeof value === "string") return rule.equalsAnyOf.includes(value);
    if (Array.isArray(value)) return value.some((v) => typeof v === "string" && rule.equalsAnyOf.includes(v));
    return false;
  });
}

/**
 * Given a project type's full question set and the current answers,
 * returns the ids of questions that are no longer visible but still have
 * a stored answer - i.e. answers that have become stale because their
 * controlling question changed. The caller (the wizard's state layer) is
 * responsible for actually clearing them; this function only identifies
 * which ones.
 */
export function findStaleAnswerIds(questions: QuestionDefinition[], answers: Record<string, unknown>): string[] {
  return questions
    .filter((q) => Object.prototype.hasOwnProperty.call(answers, q.id) && !isQuestionVisible(q, answers))
    .map((q) => q.id);
}
